import axios, {
    AxiosInstance,
    AxiosError,
    InternalAxiosRequestConfig,
    AxiosHeaders,
    AxiosResponse,
} from "axios";
import { getError } from "./api.helper";
import { ErrorResponse, TokenResponse } from "../../interfaces/auth.interface";

export class AxiosHelper {
    static instance: AxiosInstance;
    private isRefreshing: boolean;
    private refreshSubscribers: ((token: string) => void)[];

    constructor(baseURL: string) {
        this.isRefreshing = false;
        this.refreshSubscribers = [];

        if (!AxiosHelper.instance) {
            AxiosHelper.instance = axios.create({
                baseURL,
                headers: new AxiosHeaders(),
            });
            this.setupInterceptors();
        }
    }

    private onRefreshed(accessToken: string): void {
        this.refreshSubscribers.forEach((callback) => callback(accessToken));
        this.refreshSubscribers = [];
    }

    private addToSubscribers(callback: (token: string) => void): void {
        this.refreshSubscribers.push(callback);
    }

    private async refreshTokenRequest(): Promise<TokenResponse> {
        const refreshToken =
            localStorage.getItem("refreshToken") ||
            sessionStorage.getItem("refreshToken");

        if (!refreshToken) {
            throw new Error("No refresh token found");
        }

        const response = await axios.post<TokenResponse>(
            `${AxiosHelper.instance.defaults.baseURL}/api/auth/refresh-token`,
            { refreshToken }
        );

        return response.data;
    }

    private setupInterceptors(): void {
        AxiosHelper.instance.interceptors.response.use(
            (response: AxiosResponse) => response.data,
            async (error: AxiosError<ErrorResponse>) => {
                if (!error.config) {
                    throw getError(error);
                }

                const originalRequest = error.config as InternalAxiosRequestConfig & {
                    _retry?: boolean;
                };
                if (
                    error.response?.status === 401 &&
                    (error.response.data?.message === "Token expired" ||
                        error.response.data?.message === "Token süresi dolmuş" ||
                        error.response.data?.code === "TOKEN_EXPIRED") &&
                    !originalRequest._retry
                ) {
                    if (this.isRefreshing) {
                        try {
                            return new Promise((resolve) => {
                                this.addToSubscribers((token: string) => {
                                    if (originalRequest.headers instanceof AxiosHeaders) {
                                        originalRequest.headers.set(
                                            "Authorization",
                                            `Bearer ${token}`
                                        );
                                    } else {
                                        originalRequest.headers = new AxiosHeaders({
                                            Authorization: `Bearer ${token}`,
                                        });
                                    }
                                    resolve(AxiosHelper.instance(originalRequest));
                                });
                            });
                        } catch (error) {
                            return Promise.reject(error);
                        }
                    }

                    originalRequest._retry = true;
                    this.isRefreshing = true;

                    try {
                        const tokens = await this.refreshTokenRequest();

                        localStorage.setItem("accessToken", tokens.accessToken);
                        localStorage.setItem("refreshToken", tokens.refreshToken);

                        this.onRefreshed(tokens.accessToken);

                        if (originalRequest.headers instanceof AxiosHeaders) {
                            originalRequest.headers.set(
                                "Authorization",
                                `Bearer ${tokens.accessToken}`
                            );
                        } else {
                            originalRequest.headers = new AxiosHeaders({
                                Authorization: `Bearer ${tokens.accessToken}`,
                            });
                        }

                        return AxiosHelper.instance(originalRequest);
                    } catch (refreshError) {
                        localStorage.removeItem("accessToken");
                        localStorage.removeItem("refreshToken");
                        window.location.href = "/login";
                        throw getError(refreshError as AxiosError);
                    } finally {
                        this.isRefreshing = false;
                    }
                }

                if (
                    error.response?.status === 401 &&
                    (error.response.data?.message === "Invalid refresh token" ||
                        error.response.data?.message === "Geçersiz refresh token")
                ) {
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("refreshToken");
                    window.location.href = "/login";
                    return Promise.reject(error);
                }

                return Promise.reject(getError(error));
            }
        );

        AxiosHelper.instance.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                const token = localStorage.getItem("accessToken");

                if (token) {
                    if (config.headers instanceof AxiosHeaders) {
                        config.headers.set("Authorization", `Bearer ${token}`);
                    } else {
                        config.headers = new AxiosHeaders({
                            Authorization: `Bearer ${token}`,
                        });
                    }
                }

                return config;
            },
            (error: AxiosError) => {
                return Promise.reject(getError(error));
            }
        );
    }

    public static getInstance(): AxiosInstance {
        if (!AxiosHelper.instance) {
            throw new Error("AxiosHelper must be initialized with a baseURL first");
        }
        return AxiosHelper.instance;
    }
}

const backendURL: string =
    process.env.REACT_APP_API_URL || "http://localhost:8080";
const axiosHelper = new AxiosHelper(backendURL);

export { axiosHelper };
export default axiosHelper;
